import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Input, Spin, message, Avatar } from "antd";
import {
  SendOutlined,
  CameraOutlined,
  PhoneOutlined,
  PaperClipOutlined,
  CloseOutlined,
  UserOutlined,
  BellOutlined,
  BellFilled,
  TeamOutlined,
} from "@ant-design/icons";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../helpers/firebase";
import { APP_NAME } from "../helpers/constants";
import Logo from "../components/general/Logo";
import {
  ChatContainer,
  ChatHeader,
  HeaderContent,
  HeaderTitle,
  ParticipantsList,
  ParticipantItem,
  ParticipantTags,
  ParticipantTag,
  NotificationSettings,
  NotificationButton,
  NotificationStatus,
  MessagesContainer,
  MessageBubble,
  MessageAvatar,
  MessageContent,
  MessageHeader,
  MessageSender,
  MessageTime,
  MessageText,
  MessageImages,
  MessageImage,
  TypingIndicator,
  TypingDots,
  InputArea,
  InputContainer,
  InputActions,
  AttachButton,
  SendButton,
  Disclaimer,
  DisclaimerText,
  LoadingSpinnerContainer,
  LoadingText,
  TriageOuter,
  TriageBanner,
  TriageButton,
  PhoneRow,
  PhoneInput,
  PublicLeoErrorPanel,
  PublicLeoErrorTitle,
  PublicLeoErrorText,
  PendingImageSection,
  PendingThumb,
  PendingImg,
  RemoveThumb,
  renderLeoMarkdown,
} from "../components/chat/tenantChatSharedStyles";

const { TextArea } = Input;

const API_BASE_URL = process.env.REACT_APP_API_URL;

/** Firestore publicLeoChatSessions message shape (same as send payload). */
function normalizeStoredMessage(m) {
  if (!m || typeof m !== "object") return null;
  let from = "assistant";
  if (m.from === "user") from = "user";
  else if (m.from === "landlord") from = "landlord";
  else if (m.from === "assistant") from = "assistant";
  return {
    from,
    text: String(m.text ?? ""),
    timestamp:
      typeof m.timestamp === "string"
        ? m.timestamp
        : new Date().toISOString(),
    images: Array.isArray(m.images) ? m.images : [],
  };
}

/** Prepend the on-load Leo greeting only if history does not already include it. */
function withLeoGreeting(initialMessage, restored) {
  const greeting = {
    from: "assistant",
    text: initialMessage,
    timestamp: new Date().toISOString(),
  };
  if (!restored || restored.length === 0) return [greeting];
  const first = restored[0];
  if (
    first?.from === "assistant" &&
    String(first.text || "").trim() === String(initialMessage || "").trim()
  ) {
    return restored;
  }
  return [greeting, ...restored];
}

function toSender(m) {
  if (m.from === "user") return "tenant";
  if (m.from === "landlord") return "landlord";
  return "ai";
}

const formatTime = (timestamp) => {
  if (!timestamp) return "Just now";
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const shortDate = date.toLocaleDateString([], {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const shortTime = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const now = new Date();
  const diffInMinutes = (now - date) / (1000 * 60);
  const diffInHours = diffInMinutes / 60;

  if (diffInMinutes < 1) {
    return `${shortDate} ${shortTime}`;
  }
  if (diffInMinutes < 60) {
    const minutes = Math.floor(diffInMinutes);
    return `${shortDate} ${shortTime} · ${minutes} ${
      minutes === 1 ? "min" : "min"
    } ago`;
  }
  if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${shortDate} ${shortTime} · ${hours} ${
      hours === 1 ? "hr" : "hr"
    } ago`;
  }
  return `${shortDate} ${shortTime}`;
};

function getSenderName(sender) {
  switch (sender) {
    case "ai":
      return "Leo AI";
    case "tenant":
      return "You";
    case "landlord":
      return "Property manager";
    default:
      return "Unknown";
  }
}

function getSenderIcon(sender) {
  switch (sender) {
    case "ai":
      return (
        <img
          src="/icons/leo/leo.png"
          alt="Leo AI"
          style={{
            width: "16px",
            height: "16px",
            filter: "brightness(0) invert(1)",
          }}
        />
      );
    case "landlord":
      return (
        <TeamOutlined
          style={{ filter: "brightness(0) invert(1)", scale: "0.85" }}
        />
      );
    case "tenant":
    default:
      return (
        <UserOutlined
          style={{ filter: "brightness(0) invert(1)", scale: "0.8" }}
        />
      );
  }
}

/**
 * Public magic-link /chat/:propertySlug session. Shares layout/styles with
 * `TenantChatWithToken` via `tenantChatSharedStyles`.
 */
export default function TenantChatPublicLeo() {
  const { propertySlug } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationPermission, setNotificationPermission] =
    useState("default");

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sessionId, setSessionId] = useState(
    () =>
      (propertySlug &&
        localStorage.getItem(`leo-chat-session-${propertySlug}`)) ||
      `lcs-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );

  const [pendingImages, setPendingImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const [triageActive, setTriageActive] = useState(false);
  const [triagePhoneCollected, setTriagePhoneCollected] = useState(false);
  const [triagePhotoCollected, setTriagePhotoCollected] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  const lastPolledHistoryRef = useRef("");
  sessionIdRef.current = sessionId;

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      const p = Notification.permission;
      setNotificationPermission(
        p === "granted" ? "granted" : p === "denied" ? "denied" : "default"
      );
    }
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      message.warning("This browser does not support notifications.");
      return;
    }
    if (Notification.permission === "granted") {
      setNotificationPermission("granted");
      return;
    }
    if (Notification.permission === "denied") {
      setNotificationPermission("denied");
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === "granted") {
        new Notification("Notifications enabled", {
          body: "You can receive browser notifications for this chat when supported.",
          icon: "/logo192.png",
        });
      }
    } catch (e) {
      setNotificationPermission("denied");
    }
  }, []);

  useEffect(() => {
    if (propertySlug && sessionId) {
      localStorage.setItem(`leo-chat-session-${propertySlug}`, sessionId);
    }
  }, [sessionId, propertySlug]);

  useEffect(() => {
    async function fetchConfig() {
      try {
        const res = await fetch(
          `${API_BASE_URL}/public/leo-chat/config/${propertySlug}`
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Property not found");
        }
        const data = await res.json();
        setConfig(data);

        const epochKey = `leo-chat-epoch-${propertySlug}`;
        const serverEpoch = String(data.publicLeoSessionEpoch ?? 0);
        const storedEpoch = localStorage.getItem(epochKey);
        if (storedEpoch === null && serverEpoch === "0") {
          localStorage.setItem(epochKey, serverEpoch);
        } else if (storedEpoch !== serverEpoch) {
          localStorage.setItem(epochKey, serverEpoch);
          localStorage.removeItem(`leo-chat-session-${propertySlug}`);
          lastPolledHistoryRef.current = "";
          const newSid = `lcs-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 10)}`;
          localStorage.setItem(`leo-chat-session-${propertySlug}`, newSid);
          setSessionId(newSid);
          setMessages([
            {
              from: "assistant",
              text: data.initialMessage,
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }

        const sid =
          localStorage.getItem(`leo-chat-session-${propertySlug}`) ||
          sessionIdRef.current;
        if (!sid) {
          setMessages([
            {
              from: "assistant",
              text: data.initialMessage,
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }

        const histRes = await fetch(
          `${API_BASE_URL}/public/leo-chat/session/${encodeURIComponent(
            sid
          )}?slug=${encodeURIComponent(propertySlug)}`
        );

        if (histRes.status === 410) {
          const body = await histRes.json().catch(() => ({}));
          localStorage.removeItem(`leo-chat-session-${propertySlug}`);
          lastPolledHistoryRef.current = "";
          const newSid = `lcs-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 10)}`;
          localStorage.setItem(`leo-chat-session-${propertySlug}`, newSid);
          setSessionId(newSid);
          setMessages([
            {
              from: "assistant",
              text: data.initialMessage,
              timestamp: new Date().toISOString(),
            },
          ]);
          message.info(
            body.error ||
              "This chat session ended. Starting a new conversation."
          );
          return;
        }

        if (!histRes.ok) {
          setMessages([
            {
              from: "assistant",
              text: data.initialMessage,
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }

        const histData = await histRes.json();
        const raw = Array.isArray(histData.chatHistory) ? histData.chatHistory : [];
        const restored = raw
          .map(normalizeStoredMessage)
          .filter(Boolean);
        setMessages(withLeoGreeting(data.initialMessage, restored));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    if (propertySlug) fetchConfig();
  }, [propertySlug]);

  useEffect(() => {
    if (!propertySlug || !sessionId || !config || !API_BASE_URL) return;
    const id = setInterval(async () => {
      if (isSending) return;
      try {
        const histRes = await fetch(
          `${API_BASE_URL}/public/leo-chat/session/${encodeURIComponent(
            sessionId
          )}?slug=${encodeURIComponent(propertySlug)}`
        );
        if (histRes.status === 410) return;
        if (!histRes.ok) return;
        const histData = await histRes.json();
        const raw = Array.isArray(histData.chatHistory)
          ? histData.chatHistory
          : [];
        const key = JSON.stringify(raw);
        if (key === lastPolledHistoryRef.current) return;
        lastPolledHistoryRef.current = key;
        const restored = raw.map(normalizeStoredMessage).filter(Boolean);
        setMessages(withLeoGreeting(config.initialMessage, restored));
      } catch (e) {
        /* ignore */
      }
    }, 6000);
    return () => clearInterval(id);
  }, [propertySlug, sessionId, config, isSending]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isSending]);

  const sendMessage = useCallback(
    async (text, extraPayload = {}) => {
      if (!text.trim() || !config || isSending) return;

      const imagesToSend =
        extraPayload.images ??
        (pendingImages.length > 0 ? pendingImages.map((p) => p.url) : undefined);

      const userMsg = {
        from: "user",
        text: text.trim(),
        timestamp: new Date().toISOString(),
        images: imagesToSend || [],
      };
      setMessages((prev) => [...prev, userMsg]);
      setInputValue("");
      setPendingImages([]);
      setIsSending(true);

      try {
        const chatHistory = messages.map((m) => ({
          from: m.from,
          text: m.text,
          timestamp: m.timestamp,
          images: m.images || [],
        }));

        const payload = {
          ...(config.unitId
            ? { unitId: config.unitId }
            : { propertyId: config.propertyId }),
          sessionId,
          message: text.trim(),
          chatHistory,
          ...extraPayload,
        };
        if (imagesToSend && imagesToSend.length > 0 && !extraPayload.images) {
          payload.images = imagesToSend;
        }

        const res = await fetch(
          `${API_BASE_URL}/public/leo-chat/send-message`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );

        const data = await res.json();
        if (res.status === 410 && data.code === "SESSION_ENDED") {
          if (propertySlug) {
            localStorage.removeItem(`leo-chat-session-${propertySlug}`);
            const newSid = `lcs-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 10)}`;
            localStorage.setItem(`leo-chat-session-${propertySlug}`, newSid);
            setSessionId(newSid);
          }
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.from === "user" && last.text === userMsg.text) {
              return prev.slice(0, -1);
            }
            return prev;
          });
          message.warning(
            data.error ||
              "This chat session ended. You can continue with a fresh conversation."
          );
          return;
        }
        if (!res.ok) throw new Error(data.error || "Failed to send message");

        const assistantMsg = {
          from: "assistant",
          text: data.reply,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMsg]);

        if (data.triageTriggered && !triageActive) {
          setTriageActive(true);
        }
      } catch (err) {
        message.error(
          err.message?.length < 120 && !err.message.includes("{")
            ? err.message
            : "Something went wrong. Please try again."
        );
        console.error("[TenantChatPublicLeo] send error:", err);
      } finally {
        setIsSending(false);
      }
    },
    [
      config,
      isSending,
      messages,
      pendingImages,
      sessionId,
      triageActive,
      propertySlug,
    ]
  );

  const handleSend = () => {
    sendMessage(inputValue);
  };

  const uploadImageFile = async (file) => {
    if (!file.type.startsWith("image/")) {
      message.error("Please upload an image file.");
      return null;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error("Image must be smaller than 10 MB.");
      return null;
    }
    const fileName = `${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 9)}_${file.name}`;
    const storageRef = ref(storage, `public-leo-chat-images/${fileName}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const handleComposeAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = 4 - pendingImages.length;
    if (remaining <= 0) {
      message.warning("You can attach up to 4 images per message.");
      return;
    }
    const toUpload = files.slice(0, remaining);

    setIsUploading(true);
    try {
      const results = await Promise.all(
        toUpload.map(async (file) => {
          const url = await uploadImageFile(file);
          if (!url) return null;
          return {
            url,
            previewUrl: URL.createObjectURL(file),
            name: file.name,
          };
        })
      );
      const valid = results.filter(Boolean);
      if (valid.length) {
        setPendingImages((prev) => [...prev, ...valid]);
      }
    } catch (err) {
      message.error("Failed to upload image. Please try again.");
      console.error("[TenantChatPublicLeo] compose upload error:", err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleTriagePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadImageFile(file);
      if (!url) return;
      setTriagePhotoCollected(true);
      await sendMessage("I've uploaded a photo of the issue.", {
        images: [url],
      });
    } catch (err) {
      message.error("Failed to upload photo. Please try again.");
      console.error("[TenantChatPublicLeo] triage upload error:", err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingImage = (idx) => {
    setPendingImages((prev) => {
      const next = [...prev];
      if (next[idx]?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(next[idx].previewUrl);
      }
      next.splice(idx, 1);
      return next;
    });
  };

  const handlePhoneSubmit = () => {
    const cleaned = phoneInput.replace(/\D/g, "");
    if (cleaned.length < 10) {
      message.error("Please enter a valid phone number.");
      return;
    }
    setTriagePhoneCollected(true);
    sendMessage(`My phone number is ${phoneInput}`, {
      tenantPhone: cleaned,
    });
    setPhoneInput("");
  };

  if (loading) {
    return (
      <ChatContainer>
        <Helmet>
          <title>Chat with Leo AI | {APP_NAME}</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <LoadingSpinnerContainer>
          <Spin size="large" />
          <LoadingText>Loading chat...</LoadingText>
        </LoadingSpinnerContainer>
      </ChatContainer>
    );
  }

  if (error) {
    return (
      <ChatContainer>
        <Helmet>
          <title>Chat with Leo AI | {APP_NAME}</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <PublicLeoErrorPanel>
          <PublicLeoErrorTitle>Chat unavailable</PublicLeoErrorTitle>
          <PublicLeoErrorText>{error}</PublicLeoErrorText>
        </PublicLeoErrorPanel>
      </ChatContainer>
    );
  }

  return (
    <>
      <Helmet>
        <title>Chat with Leo AI | {APP_NAME}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <ChatContainer>
        <ChatHeader>
          <HeaderContent>
            <Logo
              color="white"
              size="medium"
              linkTo={null}
              style={{ pointerEvents: "none !important" }}
            />
            <HeaderTitle>Chat with Your Landlord &amp; Leo AI</HeaderTitle>
            <ParticipantsList>
              <ParticipantItem>
                <ParticipantTags>
                  <ParticipantTag>You</ParticipantTag>
                  <ParticipantTag>Landlord</ParticipantTag>
                  <ParticipantTag>
                    <img
                      src="/icons/leo/leo.png"
                      alt="Leo AI"
                      style={{
                        marginRight: "4px",
                        width: "12px",
                        height: "12px",
                        filter: "brightness(0) invert(1)",
                      }}
                    />
                    Leo AI
                  </ParticipantTag>
                </ParticipantTags>
              </ParticipantItem>
            </ParticipantsList>
            <NotificationSettings>
              {notificationPermission === "default" && (
                <NotificationButton
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    requestNotificationPermission();
                  }}
                >
                  <BellOutlined style={{ filter: "brightness(0) invert(1)" }} />{" "}
                  <span>Enable Notifications</span>
                </NotificationButton>
              )}
              {notificationPermission === "denied" && (
                <NotificationStatus>
                  <BellOutlined
                    style={{ opacity: 0.5, filter: "brightness(0) invert(1)" }}
                  />{" "}
                  <span>Notifications blocked</span>
                </NotificationStatus>
              )}
              {notificationPermission === "granted" && (
                <NotificationStatus>
                  <BellFilled style={{ filter: "brightness(0) invert(1)" }} />{" "}
                  <span>Notifications enabled</span>
                </NotificationStatus>
              )}
            </NotificationSettings>
          </HeaderContent>
        </ChatHeader>

        <MessagesContainer>
          {messages.map((msg, i) => {
            const sender = toSender(msg);
            return (
              <MessageBubble key={i} $sender={sender}>
                <MessageAvatar $sender={sender}>
                  <Avatar
                    size="small"
                    icon={getSenderIcon(sender)}
                    style={{
                      backgroundColor:
                        sender === "ai"
                          ? "var(--color-primary)"
                          : sender === "landlord"
                            ? "#2563eb"
                            : "#2C5F2D",
                    }}
                  />
                </MessageAvatar>
                <MessageContent $sender={sender}>
                  <MessageHeader $sender={sender}>
                    <MessageSender>
                      {getSenderName(sender)}
                    </MessageSender>
                    <MessageTime>{formatTime(msg.timestamp)}</MessageTime>
                  </MessageHeader>
                  {msg.text && String(msg.text).trim() && (
                    <MessageText $sender={sender}>
                      {renderLeoMarkdown(msg.text)}
                    </MessageText>
                  )}
                  {msg.images && msg.images.length > 0 && (
                    <MessageImages $tenant={sender === "tenant"}>
                      {msg.images.map((url, ii) => (
                        <MessageImage
                          key={ii}
                          src={url}
                          alt={`Attached image ${ii + 1}`}
                          onClick={() => window.open(url, "_blank")}
                        />
                      ))}
                    </MessageImages>
                  )}
                </MessageContent>
              </MessageBubble>
            );
          })}

          {isSending && (
            <MessageBubble $sender="ai">
              <MessageAvatar $sender="ai">
                <Avatar
                  size="small"
                  icon={getSenderIcon("ai")}
                  style={{ backgroundColor: "var(--color-primary)" }}
                />
              </MessageAvatar>
              <MessageContent $sender="ai">
                <TypingIndicator>
                  Leo AI is typing
                  <TypingDots>
                    <span />
                    <span />
                    <span />
                  </TypingDots>
                </TypingIndicator>
              </MessageContent>
            </MessageBubble>
          )}

          <div ref={messagesEndRef} />
        </MessagesContainer>

        {triageActive && !triagePhotoCollected && (
          <TriageOuter>
            <TriageBanner>
              <CameraOutlined />
              <span>
                Upload a photo of the issue so your landlord can see what&apos;s
                going on.
              </span>
              <TriageButton
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.accept = "image/*";
                    fileInputRef.current.capture = "environment";
                    fileInputRef.current.onchange = handleTriagePhotoUpload;
                    fileInputRef.current.click();
                  }
                }}
              >
                {isUploading ? <Spin size="small" /> : "Upload photo"}
              </TriageButton>
            </TriageBanner>
          </TriageOuter>
        )}

        {triageActive && !triagePhoneCollected && (
          <TriageOuter>
            <TriageBanner>
              <PhoneOutlined />
              <span>
                Share your phone number so the landlord can follow up quickly.
              </span>
              <PhoneRow>
                <PhoneInput
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  placeholder="(555) 123-4567"
                  maxLength={14}
                />
                <TriageButton onClick={handlePhoneSubmit}>Submit</TriageButton>
              </PhoneRow>
            </TriageBanner>
          </TriageOuter>
        )}

        <InputArea>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleComposeAttach}
          />
          {pendingImages.length > 0 && (
            <PendingImageSection>
              {pendingImages.map((img, idx) => (
                <PendingThumb key={idx}>
                  <PendingImg src={img.previewUrl} alt={img.name} />
                  <RemoveThumb
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => removePendingImage(idx)}
                  />
                </PendingThumb>
              ))}
            </PendingImageSection>
          )}

          <InputContainer>
            <InputActions>
              <AttachButton
                type="text"
                icon={
                  isUploading ? <Spin size="small" /> : <PaperClipOutlined />
                }
                disabled={isSending || isUploading || pendingImages.length >= 4}
                $active={false}
                title="Attach image"
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.removeAttribute("capture");
                    fileInputRef.current.accept = "image/*";
                    fileInputRef.current.multiple = true;
                    fileInputRef.current.onchange = handleComposeAttach;
                    fileInputRef.current.click();
                  }
                }}
              />
            </InputActions>

            <TextArea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Chat with landlord &amp; Leo AI…"
              title="Please do not share sensitive medical or health information."
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={isSending}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <SendButton
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={isSending}
              disabled={
                (!inputValue.trim() && pendingImages.length === 0) || isSending
              }
            >
              <span className="desktop-text">Send</span>
            </SendButton>
          </InputContainer>

          <Disclaimer>
            <DisclaimerText>
              <span className="desktop-text" style={{ maxWidth: "1000px" }}>
                Please do not share sensitive medical or health information. Leo
                AI can make mistakes. Check important info.
              </span>
              <span className="mobile-text">
                Please do not share sensitive medical or health information. Leo
                AI can make mistakes. Check important info.
              </span>
            </DisclaimerText>
          </Disclaimer>
        </InputArea>
      </ChatContainer>
    </>
  );
}
